import { useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import classNames from 'classnames'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'

import { Body, Button, Divider, TextArea } from '@foxino/components-web'

import { BugReportMessageType, GetUserProfileQuery } from '@app/data'
import { useTranslation } from '@app/locales'
import { useBugReporting } from '@app/modules/classroom/data'
import { showToast } from '@app/utils/showToast'

type ReportBugFormValues = {
  bugType: BugReportMessageType
  message: string
}

type Props = {
  userProfile: GetUserProfileQuery['userProfile']
}

export const BugReportForm = ({ userProfile }: Props) => {
  const { t } = useTranslation(['settings', 'common'])

  const { actions, loading } = useBugReporting()

  const defaultValues = useMemo<ReportBugFormValues>(() => {
    return {
      bugType: BugReportMessageType.BUG,
      message: ''
    }
  }, [userProfile])

  const {
    handleSubmit,
    reset,
    setValue,
    register,
    control,
    formState: { dirtyFields, errors }
  } = useForm<ReportBugFormValues>({ defaultValues })

  if (!isEmpty(errors)) {
    console.info('ProfileForm - errors: ', errors)
  }

  const isTouched = !isEqual(dirtyFields, {})
  const bugType = useWatch({ name: 'bugType', control })

  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues])

  const handleOnSubmit = async (data: ReportBugFormValues) => {
    const success = await actions.reportBug(data.bugType, data.message)

    if (success) {
      showToast(t('reportBug.thankYouMessage'), { type: 'success' })
      reset()
    }
  }

  return (
    <form onSubmit={handleSubmit(handleOnSubmit)} className="flex size-full flex-col p-5 text-white">
      <div className="flex items-center justify-between">
        <Body size="3xl" weight="bold">
          {t('reportBug.title')}
        </Body>
      </div>

      <Divider className="my-4 bg-primary-800" />

      <Body size="lg" weight="semibold" className="mb-2 mt-2 text-white">
        {t('reportBug.label')}
      </Body>
      <Body size="sm" weight="regular" className="mb-6 text-primary-300">
        {t('reportBug.description')}
      </Body>

      {/* Bug type */}
      <div className="mb-4 flex items-center gap-2">
        <Button color="secondary" className="gap-2" onClick={() => setValue('bugType', BugReportMessageType.BUG)}>
          <div
            className={classNames(
              'h-4 w-4 rounded-full border-primary-400 bg-white',
              bugType === BugReportMessageType.BUG && 'border-4'
            )}
          />
          {t('reportBug.bugType')}
        </Button>
        <Button
          color="secondary"
          className="gap-2"
          onClick={() => setValue('bugType', BugReportMessageType.SUGGESTION)}
        >
          <div
            className={classNames(
              'h-4 w-4 rounded-full border-primary-400 bg-white',
              bugType === BugReportMessageType.SUGGESTION && 'border-4'
            )}
          />
          {t('reportBug.featureRequest')}
        </Button>
      </div>

      {/* Description */}
      <TextArea
        {...register('message')}
        autoFocus
        label={t('reportBug.bugDescription')}
        placeholder={t('reportBug.placeholder')}
        textareaClassName="bg-primary-900"
        wrapperClassName="bg-primary-900"
        rows={8}
        maxLength={1000}
      />

      <div className="flex w-full justify-end">
        <Button disabled={loading || !isTouched} type="submit" color="primary" className="mt-4" loading={loading}>
          {t('submit')}
        </Button>
      </div>
    </form>
  )
}
